import React, { useRef, useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { getThemeProps } from '@elo-kit/utils/block.utils'

import { ProgressBarComponent } from '../progress-bar'
import { BLOCK_MENU_HIGHLIGHT_IDS } from '../../constants/block.constants'

import { useHammer } from './hooks/useHammer'

import { PDF_CONTAINER_MAX_HEIGHT, PDF_TOOLBAR_HEIGHT, useReaderStyles } from './PdfReader.styles'
import { FullScreenControls, ZoomControls, FileName, Pagination, DocumentContent } from './components'

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_SCALE,
  ANIMATED_ZOOM_STEP,
  MIN_ZOOM_STEP,
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_ACTIONS,
  HAMMER_OPTIONS,
  DEFAULT_SCROLL_POSITION,
} from './constants/constants'

export const PdfReaderComponent = (props) => {
  const {
    id,
    locales,
    fileName,
    fileUrl,
    previewMode,
    pageBuilderPreview,
    customClassName,
    NumberFieldComponent,
    block,
  } = props

  const innerContainer = useRef(null)
  const outerContainer = useRef(null)
  const rootContainer = useRef(null)

  const [position, setPosition] = useState(DEFAULT_SCROLL_POSITION)
  const [fullScreen, setFullScreen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [scale, setScale] = useState(DEFAULT_SCALE)
  const [numPages, setNumPages] = useState(null)
  const [paginationData, setPaginationData] = useState({
    pageNumber: DEFAULT_PAGE_NUMBER,
    paginatorValue: DEFAULT_PAGE_NUMBER,
  })

  const classes = block ? useReaderStyles({ theme: getThemeProps(block) }) : {}
  const containerId = `pdf-reader-document-wrapper_${id}`

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFullScreenChange)
    document.addEventListener('webkitfullscreenchange', onFullScreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange)
      document.removeEventListener('webkitfullscreenchange', onFullScreenChange)
    }
  }, [])

  useEffect(() => {
    setNumPages(null)
    setPaginationData({
      pageNumber: DEFAULT_PAGE_NUMBER,
      paginatorValue: DEFAULT_PAGE_NUMBER,
    })
  }, [])

  const handlePanEnd = (e) => {
    if (!isPdfReady()) {
      return
    }

    const isScrollBarScrolling = e.pointerType === 'mouse' && e.target.classList[0] === containerId

    if (isScrollBarScrolling) {
      return false
    }

    const hammerEvent = e.pointers[0]

    innerContainer.current.style.removeProperty('user-select')
    innerContainer.current.style.cursor = 'grab'

    const leftPosition = position.left - (hammerEvent.clientX - position.x)
    const topPosition = position.top - (hammerEvent.clientY - position.y)

    innerContainer.current.scrollTo({
      top: topPosition,
      left: leftPosition,
      behavior: 'smooth',
    })
  }

  const handlePanStart = (e) => {
    if (!isPdfReady()) {
      return
    }

    const isScrollBarScrolling = e.pointerType === 'mouse' && e.target.classList[0] === containerId

    if (isScrollBarScrolling) {
      return false
    }

    const currentEvent = e.pointers[0]
    innerContainer.current.style.cursor = 'grabbing'
    innerContainer.current.style.userSelect = 'none'

    setPosition({
      left: innerContainer.current.scrollLeft,
      top: innerContainer.current.scrollTop,
      x: currentEvent.clientX,
      y: currentEvent.clientY,
    })
  }

  const handlePinch = (e) => {
    if (!isPdfReady()) return

    setScale(e.scale * 100)
  }

  const handleSwipeLeft = () => setPageNumber(paginationData.pageNumber + 1)

  const handleSwipeRight = () => setPageNumber(paginationData.pageNumber - 1)

  const handleDoubleTap = () => setZoom(scale + MIN_ZOOM_STEP, ZOOM_ACTIONS.INC)

  useHammer(innerContainer, HAMMER_OPTIONS, {
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    onPanStart: handlePanStart,
    onPanEnd: handlePanEnd,
    onPinch: handlePinch,
    onDoubleTap: handleDoubleTap,
  })

  const setPageNumber = (value) => {
    if (value >= numPages) {
      setPaginationData({
        pageNumber: numPages,
        paginatorValue: numPages,
      })
    } else if (value > 1) {
      setPaginationData({
        pageNumber: value,
        paginatorValue: value,
      })
    } else {
      setPaginationData({
        pageNumber: 1,
        paginatorValue: 1,
      })
    }
  }

  const isPdfReady = () => !(loading || error || !fileUrl || (previewMode && !pageBuilderPreview))

  const onFullScreenChange = () => {
    setFullScreen(
      document.fullscreen ||
        document.webkitIsFullScreen ||
        (document.fullscreenElement !== null && document.fullscreenElement !== undefined) ||
        (document.webkitCurrentFullScreenElement !== null && document.webkitCurrentFullScreenElement !== undefined)
    )
  }

  const handleFullScreenExit = () => setScale(DEFAULT_SCALE)

  const handleFullScreenEnter = () => setScale(DEFAULT_SCALE * 2)

  const handleSetFullScreen = () => {
    const container = outerContainer.current
    if (container && container.requestFullscreen) {
      if (!document.fullscreenElement) {
        container.requestFullscreen()?.then(() => {
          handleFullScreenEnter()
        })
      } else {
        document.exitFullscreen()?.then(() => {
          handleFullScreenExit()
        })
      }
    } else if (container && container.webkitRequestFullscreen) {
      if (!document.webkitCurrentFullScreenElement) {
        container?.webkitRequestFullscreen()?.then(() => {
          handleFullScreenEnter()
        })
      } else {
        document.webkitExitFullscreen()?.then(() => {
          handleFullScreenExit()
        })
      }
    }
  }

  const setZoom = (value, action) => {
    let currentScale = scale
    const zoomInterval = setInterval(() => {
      const shouldClearInterval =
        (value >= MAX_ZOOM && MIN_ZOOM >= value) ||
        currentScale === value ||
        (action === ZOOM_ACTIONS.INC && currentScale >= value) ||
        (action === ZOOM_ACTIONS.DEC && currentScale <= value)

      if (shouldClearInterval) {
        clearInterval(zoomInterval)
      } else {
        currentScale =
          action === ZOOM_ACTIONS.INC ? currentScale + ANIMATED_ZOOM_STEP : currentScale - ANIMATED_ZOOM_STEP
        setScale(currentScale)
      }
    }, 5)
  }

  const onDocumentLoadProgress = ({ loaded, total }) => {
    if (!(loaded >= total) && !numPages) {
      setLoading(true)
    }
  }

  const onDocumentLoadSuccess = ({ numPages }, resetTransform) => {
    setError(false)
    setLoading(false)
    setNumPages(numPages)

    if (resetTransform) {
      resetTransform()
    }
  }

  const handlePagination = (value) => setPageNumber(value)

  const onLoadError = () => setError(true)

  const onDownloadClick = () => {
    if (previewMode) {
      return
    }

    window.open(fileUrl, '_blank')
  }

  const containerHeight =
    fullScreen && rootContainer.current
      ? rootContainer.current.offsetHeight - PDF_TOOLBAR_HEIGHT
      : PDF_CONTAINER_MAX_HEIGHT - PDF_TOOLBAR_HEIGHT
  const innerContainerHeight =
    fullScreen && innerContainer.current
      ? innerContainer.current && innerContainer.current.offsetHeight - PDF_TOOLBAR_HEIGHT
      : innerContainer.current && innerContainer.current.offsetHeight

  const isNotReadyStateState = loading || error || !fileUrl

  return (
    <div className={classNames(customClassName, classes.pdfContainerRoot, 'pdf-reader-root')} ref={rootContainer}>
      {/* HACK FOR SAFARI */}
      {fullScreen && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
                .react-transform-component { height: -webkit-fill-available; }
                .pdf-reader-container {
                  min-height: initial !important;
                  max-height: initial !important;
                }
                .toolbar-container {
                  position: absolute;
                  bottom: 0;
                  width: 100%;
                }
                .${containerId} {
                  min-height: initial !important;
                  max-height: initial !important;
                }
              `,
          }}
        />
      )}

      <div className={classNames(customClassName, classes.pdfContainer, 'pdf-reader-container')} ref={outerContainer}>
        <div className={classNames('inner-container', classes.innerContainer)}>
          <div className={classNames('background-overlay', classes.backgroundOverlay)} />
          <div
            className={classNames(containerId, classes.documentWrapper)}
            style={{
              cursor: isPdfReady() ? 'grab' : 'initial',
              minHeight: containerHeight,
              maxHeight: containerHeight,
            }}
            ref={innerContainer}
          >
            <div
              style={{
                width: (outerContainer.current && outerContainer.current.offsetWidth) || '100%',
                height: innerContainerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: error || !fileUrl ? 'center' : '',
              }}
            >
              <DocumentContent
                fileUrl={fileUrl}
                fileName={fileName}
                containerHeight={containerHeight}
                classes={classes}
                previewMode={previewMode}
                locales={locales}
                scale={scale}
                pageNumber={paginationData.pageNumber}
                onLoadError={onLoadError}
                onDocumentLoadProgress={onDocumentLoadProgress}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
              />
            </div>
          </div>
        </div>
        <div
          className={classNames('toolbar-container', classes.toolbarContainer)}
          {...(previewMode && {
            'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.filePdf.pdfToolbar,
            'data-highlighter-selector': '',
          })}
        >
          <div
            className={classNames('toolbar-progress', {
              error: isNotReadyStateState,
            })}
            {...(previewMode && {
              'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.filePdf.pdfProgressBar,
              'data-highlighter-selector': '.progress-bar-container',
            })}
          >
            <ProgressBarComponent now={paginationData.pageNumber} max={numPages} classes={classes} />
          </div>
          <div className={classNames('toolbar-container-items', classes.toolbarContainerItems)}>
            <div
              className={classNames('toolbar-left', classes.toolbarLeft)}
              {...(previewMode && {
                'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.filePdf.downloadButtonPdf,
                'data-highlighter-selector': '',
              })}
            >
              <div className={classNames('toolbar-left', classes.downloadAction)}>
                <span className={classNames('action-btn', classes.actionButton)}>
                  <FileName fileName={fileName} classes={classes} />
                  <i className='fas fa-download' onClick={onDownloadClick} />
                </span>
              </div>
            </div>
            <div className={classNames('toolbar-center', classes.toolbarCenter)}>
              {!isNotReadyStateState && (
                <Pagination
                  previewMode={previewMode}
                  locales={locales}
                  classes={classes}
                  loading={loading}
                  numPages={numPages}
                  NumberFieldComponent={NumberFieldComponent}
                  pageNumber={paginationData.pageNumber}
                  paginatorValue={paginationData.paginatorValue}
                  handlePagination={handlePagination}
                />
              )}
            </div>
            <div className={classNames('toolbar-right', classes.toolbarRight)}>
              {!isNotReadyStateState && (
                <Fragment>
                  <ZoomControls
                    classes={classes}
                    previewMode={previewMode}
                    loading={loading}
                    onIncreaseZoom={() => setZoom(scale + MIN_ZOOM_STEP, ZOOM_ACTIONS.INC)}
                    onDecreaseZoom={() => setZoom(scale - MIN_ZOOM_STEP, ZOOM_ACTIONS.DEC)}
                  />
                  <FullScreenControls
                    classes={classes}
                    previewMode={previewMode}
                    loading={loading}
                    onClick={handleSetFullScreen}
                  />
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

PdfReaderComponent.propTypes = {
  id: PropTypes.string,
  classes: PropTypes.object,
  previewMode: PropTypes.bool,
  NumberFieldComponent: PropTypes.any,
  pageBuilderPreview: PropTypes.bool,
  customClassName: PropTypes.string,
  fileName: PropTypes.string,
  resetTransformOnPageChange: PropTypes.bool,
  locales: PropTypes.shape({
    docIsNotVisibleTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    docIsNotVisibleContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    docIsNotVisibleBtnContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    renderPaginationInfo: PropTypes.func,
  }),
  fileUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}

PdfReaderComponent.defaultProps = {
  id: '',
  classes: {},
  fileUrl: '',
  fileName: '',
  NumberFieldComponent: 'input',
}
